import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  countyCodeShape,
  Div,
  Dropdown,
  Flex,
  Gutter,
  Input,
  OptionListOption,
  PhoneNumberInput,
} from '@gaiads/telia-react-component-library';
import { COUNTRIES } from '@cdaas/reusable-components-daas';

import { UserGroupResponse, UserResponse } from '@cdaas/api-clients/dist/asset-admin-openapi';

import CreateGroupModalDialog from './CreateGroupModalDialog/CreateGroupModalDialog';
import { addKey, compareIdAndKeyString, EMAIL_REGEX, getIdAndKeyString, getInitUser, UserResponseWithUserGroup, WithKey } from '../../../../utils';

import styles from './EditAdministrator.module.scss';

export default function EditAdministrator({
  admin,
  userGroups,
  currency,
  onCancel,
  onAdminUpdate,
  onGroupCreate,
}: {
  admin?: WithKey<UserResponseWithUserGroup>;
  userGroups: UserGroupResponse[];
  currency: string;
  onCancel: () => void;
  onAdminUpdate: (administrator: UserResponse) => void;
  onGroupCreate: (group: WithKey<UserGroupResponse>) => void;
}) {
  const { t } = useTranslation('organizationForm');
  const inputIdPrefix = useId();

  const procurementLimitRegexPattern = '^[0-9]{1,10}[.]*[0-9]{0,2}$';

  const [inputLimit, setInputLimit] = useState(admin?.policies.lease_procurement_limit?.toString() ?? '');
  const [inputLimitError, setInputLimitError] = useState('');
  const countryCodes: countyCodeShape[] = [...COUNTRIES, { value: 'DA', countryName: 'All', code: '42' }];

  const [selectedCountryCode, setSelectedCountryCode] = useState(
    countryCodes.find(countryCode => admin?.phone_number?.match(new RegExp(`^\\+${countryCode.code}`))),
  );
  const [phoneNumber, setPhoneNumber] = useState(admin?.phone_number?.replace(new RegExp(`^\\+${selectedCountryCode?.code}`), ''));
  const [phoneNumberError, setPhoneNumberError] = useState();

  const [email, setEmail] = useState(admin?.email || '');
  const [emailError, setEmailError] = useState();

  const [selectedUserGroup, setSelectedUserGroup] = useState<OptionListOption | undefined>(
    admin?.user_group && {
      label: admin.user_group.name,
      value: getIdAndKeyString(admin.user_group),
    },
  );
  const [userGroupError, setUserGroupError] = useState();

  const [isCreateGroupModalDialogOpen, setIsCreateGroupModalDialogOpen] = useState(false);

  const reset = () => {
    setPhoneNumber('');
    setSelectedCountryCode(undefined);
    setEmail('');
    setSelectedUserGroup(undefined);
  };

  const validate = () => {
    let valid = true;
    if (!selectedCountryCode || !phoneNumber) {
      setPhoneNumberError(t('invite_admin.phoneNumberInputRequiredError'));
      valid = false;
    } else {
      setPhoneNumberError(undefined);
    }
    if (!email) {
      setEmailError(t('invite_admin.email_address_input_required_error'));
      valid = false;
    } else if (EMAIL_REGEX.exec(email) === null) {
      setEmailError(t('invite_admin.email_address_invalid_error'));
      valid = false;
    } else {
      setEmailError(undefined);
    }

    if (inputLimit && !inputLimit.match(procurementLimitRegexPattern)) {
      setInputLimitError(t('invite_admin.procurement_limit_invalid_error'));
      valid = false;
    }

    if (!selectedUserGroup) {
      setUserGroupError(t('invite_admin.groupRequiredError'));
      valid = false;
    } else {
      setUserGroupError(undefined);
    }
    return valid;
  };

  const handleUpdate = () => {
    if (validate()) {
      const user_group = userGroups.find(group => compareIdAndKeyString(getIdAndKeyString(group), selectedUserGroup?.value));
      const adminData = admin || getInitUser();
      const editedAdministator: UserResponseWithUserGroup = {
        ...adminData,
        phone_number: `+${selectedCountryCode?.code}${phoneNumber}`,
        email: email!,
        user_group,
        user_roles: {
          effective: [],
          user: [],
          user_group: [],
        },
        allowed_procurements: [],
        policies: { ...adminData?.policies!, lease_procurement_limit: inputLimit === '' ? null : +inputLimit },
      };
      onAdminUpdate(editedAdministator);
      reset();
    }
  };
  const handleCancel = () => {
    onCancel();
    reset();
  };

  const handleGroupCreate = (group: UserGroupResponse) => {
    const groupWithKey = addKey(group);
    onGroupCreate(groupWithKey);
    setSelectedUserGroup({
      label: groupWithKey.name,
      value: getIdAndKeyString(groupWithKey),
    });
    setIsCreateGroupModalDialogOpen(false);
  };

  const getUserGroupDefaultProcurementLimitText = () => {
    if (admin) {
      const limit = userGroups.find(userGroup => userGroup.id === admin.user_group?.id)?.policies?.lease_procurement_limit;
      return limit
        ? `${t('invite_admin.procurement_limit_additional_text_part1')} ${admin.user_group?.name} ${t(
            'invite_admin.procurement_limit_additional_text_part2',
          )} ${limit}`
        : '';
    }
    return '';
  };

  return (
    <>
      <Div className={styles['customer-organization-form__edit-administrator-container']}>
        <Input
          id={`${inputIdPrefix}-edit-administrator-email`}
          label={t('invite_admin.email_address_input_label')}
          value={email}
          onChange={(_event, value) => setEmail(value)}
          type="email"
          errorText={emailError}
          required
          disabled={!!admin?.id}
        />
        <Gutter />
        <Div data-testid="phoneNumberInputContainer">
          <PhoneNumberInput
            label={t('invite_admin.phoneNumberInputLabel')}
            countryCodes={countryCodes}
            onNumberChange={(event, value) => {
              setPhoneNumber(value);
            }}
            numberValue={phoneNumber}
            onCountryCodeChange={(event, selectedCode) => {
              setSelectedCountryCode(selectedCode);
            }}
            selectedCountryCode={selectedCountryCode}
            errorText={phoneNumberError}
            required
          />
        </Div>
        <Gutter />
        <Input
          id={`${inputIdPrefix}-edit-administrator-procurement-limit`}
          label={t('invite_admin.procurement_limit', { currency })}
          value={inputLimit}
          onChange={(_event, value) => setInputLimit(value)}
          errorText={inputLimitError}
          helperText={getUserGroupDefaultProcurementLimitText()}
        />
        <Gutter />
      </Div>
      <Flex alignBottom>
        <Div className={styles['customer-organization-form__edit-administrator-container']}>
          <Dropdown
            label={t('invite_admin.group_input_label')}
            name="user-group-dropdown"
            selectedOption={selectedUserGroup}
            options={userGroups.map(userGroup => ({
              label: userGroup.name,
              value: getIdAndKeyString(userGroup),
            }))}
            onChange={(event, option) => setSelectedUserGroup(option)}
            required={true}
            placeholder={t('invite_admin.group_input_placeholder')}
            errorText={userGroupError}
            disabled={!!admin}
          />
        </Div>
        {!admin && (
          <>
            <Gutter />
            <Button variant="primaryWhite" onClick={() => setIsCreateGroupModalDialogOpen(true)} margin={{ bottom: 'xxs' }}>
              {`+ ${t('invite_admin.create_new_group_btn')}`}
            </Button>
          </>
        )}
      </Flex>
      <Gutter />
      <Flex>
        <Button variant="secondary" onClick={handleUpdate}>
          {t('administrators.doneButton')}
        </Button>
        <Gutter />
        <Button variant="primaryWhite" onClick={handleCancel}>
          {t('administrators.cancelButton')}
        </Button>
      </Flex>
      <CreateGroupModalDialog
        isOpen={isCreateGroupModalDialogOpen}
        onCancel={() => setIsCreateGroupModalDialogOpen(false)}
        onCreate={handleGroupCreate}
      />
    </>
  );
}
